import { useDispatch, useSelector, useStore } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    IDomain,
    setAvailableDomains,
    setSelectedDomains,
    MappedDomains,
    setAvailableTools,
    setDefaultSelectedDomains,
    ITool,
    setOnlySearch,
    clearPrependContext,
} from 'store/conciergeSlice';
import { conciergeAPI } from 'api/concierge';
import { uid } from 'tools/tools';
import {
    JSON_IN_MESSAGE_REGEX,
    JSON_START_MESSAGE_REGEX,
    THINKING_IN_MESSAGE_REGEX,
    parseJsonMessage,
    parseMessage,
} from '../tools';
import { setWaitingConcierge } from 'store/globalSearchSlice';
import useBundleTranslation from 'i18n';
import { ErrorBlock, IDialogItem, IMessagePart, StreamThinking } from '../types';
import { useConfigConstantValue } from 'hooks/useConfigConstant';
import { useMatches } from 'react-router-dom';
import { RouteItemType } from 'routes';
import { requestConciergeContext } from '../conciergeContext';

let currentController: AbortController | null = null;

const checkLogout = (message: string) => {
    let response = {
        error: '',
        status: '',
        url: '',
        params: '',
        type: '',
    };

    try {
        response = JSON.parse(message);
    } catch (error) {
        return false;
    }

    if (!response || typeof response !== 'object') {
        return false;
    }

    if ('ERROR' == response.status && ('Unauthorized' == response.error || 'NotFound' == response.error)) {
        let url = '/auth/deny/type/' + response.type;

        if (response.params) url += '?' + new URLSearchParams(response.params);

        document.location = url;
    } else if ('ERROR' == response.status && 'need_redirect' == response.error && response.url) {
        if (document.location.href != response.url) document.location = response.url;
        else document.location.reload();
    }

    return false;
};

export interface CustomInstructions {
    mcp_post_processing?: string;
    llm_comment?: string;
}

export interface UseConciergeChartConfig {
    startNewChatAfter?: number;
    includeContentSearch?: boolean;
    includeHelpDocs?: boolean;
    includeGlossaryTerms?: boolean;
    includeCustomFaq?: boolean;
    availableDomains?: string[];
    selectedDomains?: string[];
    isAppPanel?: boolean;
    extraUrlParams?: { [key: string]: string };
    getContext?: () => any | Promise<any>;
    customInstructions?: CustomInstructions;
}

export interface UseConciergeChartResult {
    dialog: IDialogItem[];
    setDialog: React.Dispatch<React.SetStateAction<IDialogItem[]>>;
    loading: boolean;
    activeChat: number;
    setActiveChat: React.Dispatch<React.SetStateAction<number>>;
    activeChatRequest: string;
    onRequestSubmit: (request: string) => void;
    startNewChat: () => void;
    onAbort: () => void;
    silentAbort: () => void;
    isElementViewer: boolean;
    mappedDomains: MappedDomains;
    domainsData: IDomain[] | undefined;
    toolsData: ITool[] | undefined;
    onResendRequest: (excludedToolsFromAuth: string[]) => void;
    setLastInteraction: React.Dispatch<React.SetStateAction<number>>;
    clearActiveChatRequest: () => void;
}

export function useConciergeChat(config: UseConciergeChartConfig, visible: boolean): UseConciergeChartResult {
    const {
        startNewChatAfter,
        includeContentSearch,
        includeHelpDocs,
        includeGlossaryTerms,
        includeCustomFaq,
        availableDomains,
        selectedDomains,
        isAppPanel,
        extraUrlParams,
        getContext,
        customInstructions,
    } = config;

    const { t } = useBundleTranslation(['app/concierge/widget']);

    const dispatch = useDispatch();
    const store = useStore();
    const matches = useMatches();

    const onlySearch = useSelector((state: any) => state.concierge.onlySearch);
    const selectedConciergeDomains = useSelector((state: any) => state.concierge.selectedDomains) as string[];
    const isMobile = useSelector((state: any) => state.concierge.isMobile) as boolean;

    const [activeChat, setActiveChat] = useState(0);
    const [activeChatRequest, setActiveChatRequest] = useState('');
    const activeChatRequestRef = useRef('');
    const [loading, setLoading] = useState(false);
    const [dialog, setDialog] = useState<IDialogItem[]>([]);
    const [lastInteraction, setLastInteraction] = useState(Date.now());
    const [mappedDomains, setMappedDomains] = useState<MappedDomains>({});
    const [proceedMode, setProceedMode] = useState(false);
    const [excludedTools, setExcludedTools] = useState<string[]>([]);
    const [pendingNewChat, setPendingNewChat] = useState<string | null>(null);

    const { data: domainsData } = useQuery<IDomain[]>({
        queryKey: ['conciergeDomains'],
        queryFn: () => conciergeAPI.getDomains(),
        enabled: visible,
        staleTime: 5 * 60 * 1000,
    });

    const { data: toolsData } = useQuery<ITool[]>({
        queryKey: ['conciergeTools'],
        queryFn: () => conciergeAPI.getTools(),
        enabled: visible && !!domainsData,
        staleTime: 5 * 60 * 1000,
    });

    const isElementViewer: boolean = (() => {
        if (matches?.length) {
            return (matches[matches.length - 1].handle as RouteItemType)?.isElementViewer ?? false;
        }
        return false;
    })();

    if (isElementViewer) {
        const elementId = matches[matches.length - 1].params?.elementId as string;

        if (elementId && mappedDomains[elementId] === undefined) {
            conciergeAPI.getMappedDomains(elementId).then((domains: string[]) => {
                setMappedDomains((prev) => {
                    return {
                        ...prev,
                        [elementId]: domains,
                    };
                });
            });
        }
    }

    const conciergeSettings = useSelector((state: any) => state.auth?.configuration?.conciergeSettings) ?? {};
    const useStream = conciergeSettings.response_streaming_support_ind == 'Y';
    const allowJS = !!useConfigConstantValue('ALLOW_JAVASCRIPT_IN_CONCIERGE_RESPONSE');

    const responseUIDRef = useRef<string>(uid());
    const requestUIDRef = useRef<string>(uid());
    const currentPath = window.location.pathname;

    const removeProceedButtons = useCallback(() => {
        setDialog((prevDialog) => {
            const hasProceedButton = prevDialog.some((item) => {
                return item.parts.some((part) => {
                    if (part.type === 'json' && part.jsonBlocks) {
                        return part.jsonBlocks.some((block) => block.data_type === 'proceed-button');
                    }
                    return false;
                });
            });

            if (!hasProceedButton) {
                return prevDialog;
            }

            return prevDialog.filter((item) => {
                const hasProceedButtonInItem = item.parts.some((part) => {
                    if (part.type === 'json' && part.jsonBlocks) {
                        return part.jsonBlocks.some((block) => block.data_type === 'proceed-button');
                    }
                    return false;
                });
                return !hasProceedButtonInItem;
            });
        });
    }, []);

    const responseToContent = useCallback(
        (message: string) => {
            if (checkLogout(message)) {
                return;
            }

            const messageParts = parseMessage(message, activeChat.toString(), undefined, allowJS);

            setDialog((prevState) => {
                const newState = [...prevState];
                let messageUID = responseUIDRef.current;

                const index = newState.findIndex((item) => item.uid === messageUID);

                if (index > -1) {
                    for (const messagePart of messageParts) {
                        if (messagePart?.type === 'invalid-request') {
                            newState[index].parts.push(messagePart);
                            continue;
                        }

                        if (messagePart?.type !== 'json') {
                            const jsonStartIndex = newState[index].parts.findLastIndex(
                                (part) => part.type == 'json_start',
                            );

                            if (jsonStartIndex > -1) {
                                const newJsonPart = newState[index].parts[jsonStartIndex].message + messagePart.message;
                                const jsonMatch = newJsonPart.match(JSON_IN_MESSAGE_REGEX);
                                const thinkingMatch = !jsonMatch ? newJsonPart.match(THINKING_IN_MESSAGE_REGEX) : null;
                                const anyMatch = jsonMatch ?? thinkingMatch;

                                if (anyMatch) {
                                    const regIndex = newJsonPart.indexOf(anyMatch[0]);
                                    const afterJsonContent = newJsonPart.substring(regIndex + anyMatch[0].length);

                                    const parsedParts = parseJsonMessage(newJsonPart);
                                    for (const parsedPart of parsedParts) {
                                        if (parsedPart.type === 'json' && parsedPart.jsonBlocks?.length) {
                                            for (const block of parsedPart.jsonBlocks) {
                                                const existingBlockIndex = newState[index].parts.findIndex(
                                                    (part) =>
                                                        part.type === 'json' &&
                                                        part.jsonBlocks?.some(
                                                            (existingBlock) =>
                                                                JSON.stringify(existingBlock) === JSON.stringify(block),
                                                        ),
                                                );

                                                if (existingBlockIndex > -1) {
                                                    continue;
                                                }

                                                const partWithBlock = {
                                                    ...parsedPart,
                                                    jsonBlocks: [block],
                                                };

                                                if (block.data_type === 'stream_thinking') {
                                                    const idx = newState.findIndex((item) => item.uid === messageUID);
                                                    if (idx > -1) {
                                                        const existingPartIdx = newState[idx].parts.findIndex(
                                                            (part) =>
                                                                part.type === 'json' &&
                                                                part.jsonBlocks?.some((b) => b.data_type === 'stream_thinking'),
                                                        );
                                                        if (existingPartIdx > -1) {
                                                            const existingBlock = newState[idx].parts[existingPartIdx]
                                                                .jsonBlocks![0] as StreamThinking;
                                                            newState[idx].parts[existingPartIdx] = {
                                                                ...newState[idx].parts[existingPartIdx],
                                                                jsonBlocks: [
                                                                    {
                                                                        ...existingBlock,
                                                                        content: [
                                                                            ...existingBlock.content,
                                                                            ...(block as StreamThinking).content,
                                                                        ],
                                                                    },
                                                                ],
                                                            };
                                                        } else {
                                                            newState[idx].parts.push(partWithBlock);
                                                        }
                                                    }
                                                    continue;
                                                }

                                                if (block.data_type === 'next-block') {
                                                    const newUID = uid();
                                                    responseUIDRef.current = newUID;
                                                    messageUID = newUID;

                                                    newState.push({
                                                        uid: messageUID,
                                                        type: 'response',
                                                        parts: [partWithBlock],
                                                        parameters: {
                                                            requestPath: currentPath,
                                                        },
                                                    });
                                                } else {
                                                    const idx = newState.findIndex((item) => item.uid === messageUID);
                                                    if (idx > -1) {
                                                        newState[idx].parts.push(partWithBlock);
                                                    }
                                                }
                                            }
                                        } else {
                                            const idx = newState.findIndex((item) => item.uid === messageUID);
                                            if (idx > -1) {
                                                newState[idx].parts.push(parsedPart);
                                            }
                                        }
                                    }
                                    newState[index].parts.splice(jsonStartIndex, 1);
                                    if (afterJsonContent.trim()) {
                                        newState[index].parts.push({
                                            type: 'markdown',
                                            message: afterJsonContent,
                                            source: 'backend',
                                        });
                                    }
                                } else {
                                    newState[index].parts[jsonStartIndex].message = newJsonPart;
                                }
                            } else {
                                if (messagePart?.type === 'markdown') {
                                    const lastPart = newState[index].parts.pop();

                                    if (lastPart) {
                                        if (lastPart.type === 'markdown') {
                                            const resultMessage = lastPart.message + messagePart.message;

                                            const match = resultMessage.match(JSON_START_MESSAGE_REGEX);
                                            if (match) {
                                                const regIndex = resultMessage.indexOf(match[0]);

                                                const beforeMatch = resultMessage.slice(0, regIndex);
                                                if (beforeMatch) {
                                                    newState[index].parts.push({
                                                        message: beforeMatch,
                                                        type: 'markdown',
                                                        source: 'backend',
                                                    });
                                                }

                                                const afterMatch = resultMessage.slice(regIndex + match[0].length);

                                                newState[index].parts.push({
                                                    message: match[0] + afterMatch,
                                                    type: 'json_start',
                                                    source: 'backend',
                                                });
                                            } else {
                                                newState[index].parts.push({
                                                    ...lastPart,
                                                    message: resultMessage,
                                                });
                                            }
                                        } else {
                                            newState[index].parts.push(lastPart);
                                            newState[index].parts.push(messagePart);
                                        }
                                    }
                                } else {
                                    newState[index].parts.push(messagePart);
                                }
                            }
                            continue;
                        }

                        if (messagePart.type === 'json' && messagePart.jsonBlocks?.length) {
                            for (const block of messagePart.jsonBlocks) {
                                if (block.data_type === 'stream_thinking') {
                                    const idx = newState.findIndex((item) => item.uid === messageUID);
                                    if (idx > -1) {
                                        const existingPartIdx = newState[idx].parts.findIndex(
                                            (part) =>
                                                part.type === 'json' &&
                                                part.jsonBlocks?.some((b) => b.data_type === 'stream_thinking'),
                                        );
                                        if (existingPartIdx > -1) {
                                            const existingBlock = newState[idx].parts[existingPartIdx]
                                                .jsonBlocks![0] as StreamThinking;
                                            newState[idx].parts[existingPartIdx] = {
                                                ...newState[idx].parts[existingPartIdx],
                                                jsonBlocks: [
                                                    {
                                                        ...existingBlock,
                                                        content: [
                                                            ...existingBlock.content,
                                                            ...(block as StreamThinking).content,
                                                        ],
                                                    },
                                                ],
                                            };
                                        } else {
                                            newState[idx].parts.push({
                                                ...messagePart,
                                                jsonBlocks: [block],
                                            });
                                        }
                                    }
                                    continue;
                                }

                                const existingBlockIndex = newState[index].parts.findIndex(
                                    (part) =>
                                        part.type === 'json' &&
                                        part.jsonBlocks?.some(
                                            (existingBlock) => JSON.stringify(existingBlock) === JSON.stringify(block),
                                        ),
                                );

                                if (existingBlockIndex > -1) {
                                    continue;
                                }

                                const partWithBlock = {
                                    ...messagePart,
                                    jsonBlocks: [block],
                                };

                                if (block.data_type === 'next-block') {
                                    const newUID = uid();
                                    responseUIDRef.current = newUID;
                                    messageUID = newUID;

                                    newState.push({
                                        uid: messageUID,
                                        type: 'response',
                                        parts: [partWithBlock],
                                        parameters: {
                                            requestPath: currentPath,
                                        },
                                    });
                                } else {
                                    if (block.data_type === 'auth-required') {
                                        const data = block.data;

                                        data.forEach((authData, index) => {
                                            if (index === 0) {
                                                const idx = newState.findIndex((item) => item.uid === messageUID);
                                                if (idx > -1) {
                                                    newState[idx].parts.push(partWithBlock);
                                                }
                                            } else {
                                                const newUID = uid();
                                                responseUIDRef.current = newUID;
                                                messageUID = newUID;

                                                newState.push({
                                                    uid: messageUID,
                                                    type: 'response',
                                                    parts: [
                                                        {
                                                            ...messagePart,
                                                            jsonBlocks: [
                                                                {
                                                                    data_type: 'auth-required',
                                                                    data: [authData],
                                                                },
                                                            ],
                                                        },
                                                    ],
                                                    parameters: {
                                                        requestPath: currentPath,
                                                    },
                                                });
                                            }
                                        });

                                        const newUID = uid();
                                        responseUIDRef.current = newUID;
                                        messageUID = newUID;

                                        newState.push({
                                            uid: messageUID,
                                            type: 'response',
                                            parts: [
                                                {
                                                    ...messagePart,
                                                    jsonBlocks: [
                                                        {
                                                            data_type: 'proceed-button',
                                                        },
                                                    ],
                                                },
                                            ],
                                            parameters: {
                                                requestPath: currentPath,
                                            },
                                        });
                                    } else {
                                        const idx = newState.findIndex((item) => item.uid === messageUID);
                                        if (idx > -1) {
                                            newState[idx].parts.push(partWithBlock);
                                        }
                                    }
                                }
                            }
                            continue;
                        }

                        newState[index].parts.push(messagePart);
                    }
                }

                return newState;
            });
        },
        [activeChat, allowJS, currentPath],
    );

    const processRequest = useCallback(
        async (additionalParams?: { [key: string]: string }, customExcludedTools?: string[]) => {
            removeProceedButtons();
            setLoading(true);
            dispatch(setWaitingConcierge(true));
            let messageUID = responseUIDRef.current;
            const currentState = store.getState();

            const freshPrependContext = currentState.concierge.prependContext as {
                text: string;
                showInChat: boolean;
            } | null;
            const backendMessage = freshPrependContext?.text
                ? freshPrependContext.text + '   ' + activeChatRequest
                : activeChatRequest;

            if (freshPrependContext?.text) {
                dispatch(clearPrependContext());
            }
            const freshSelectedDomains = currentState.concierge.selectedDomains as string[];
            const allFreshAvailableDomains = currentState.concierge.availableDomains as IDomain[];

            const freshAvailableDomains = isMobile
                ? allFreshAvailableDomains.filter(
                      (domain) => !domain.customServices?.some((service) => service.authInfo),
                  )
                : allFreshAvailableDomains;

            let externalContext: string | undefined;
            if (getContext) {
                try {
                    const contextResult = await getContext();
                    if (contextResult !== undefined && contextResult !== null) {
                        externalContext =
                            typeof contextResult === 'string' ? contextResult : JSON.stringify(contextResult);
                    }
                } catch (e) {
                    console.error('Error calling getContext callback:', e);
                }
            }

            const conciergeContext = await requestConciergeContext();
            if (conciergeContext) {
                const conciergeContextStr = JSON.stringify(conciergeContext);
                externalContext = externalContext
                    ? `${externalContext}\n\n${conciergeContextStr}`
                    : conciergeContextStr;
            }

            const effectiveParams: { [key: string]: string } = {
                proceed: proceedMode || isMobile ? '1' : '0',
                ...additionalParams,
            };

            if (externalContext) {
                effectiveParams.context = externalContext;
            }

            const result = new Set<string>();

            freshSelectedDomains.forEach((domain) => {
                const domainConfig = freshAvailableDomains.find((d) => d.name === domain);

                if (domainConfig && domainConfig.customServices && domainConfig.customServices.length > 0) {
                    domainConfig.customServices.forEach((service) => {
                        if (isMobile && service.authInfo) return;
                        const domainId =
                            service.domain_id && service.domain_id > ''
                                ? service.domain_id
                                : domainConfig.concierge_domain_id !== '-1'
                                  ? `id_${domainConfig.concierge_domain_id}`
                                  : null;
                        result.add(domainId ? `${service.name}|${domainId}` : service.name);
                    });
                }
            });

            if (result.size === 0) {
                freshAvailableDomains.forEach((domain) => {
                    if (domain.customServices && domain.customServices.length > 0) {
                        domain.customServices.forEach((service) => {
                            if (isMobile && service.authInfo) return;
                            const domainId =
                                service.domain_id && service.domain_id > ''
                                    ? service.domain_id
                                    : domain.concierge_domain_id !== '-1'
                                      ? `id_${domain.concierge_domain_id}`
                                      : null;
                            result.add(domainId ? `${service.name}|${domainId}` : service.name);
                        });
                    }
                });
            }

            const toolsToExclude = customExcludedTools ?? excludedTools;

            const currentSelectedTools = Array.from(result).filter((tool) => {
                const toolName = tool.includes('|') ? tool.split('|')[0] : tool;
                return !toolsToExclude.includes(toolName);
            });

            currentController?.abort();
            currentController = new AbortController();
            const signal = currentController.signal;

            const responseCancelledByUser = () => {
                setDialog((prevState) => {
                    const newState = [...prevState];
                    const index = newState.findIndex((item) => item.uid == messageUID);

                    if (index > 0) {
                        newState[index].parts.push({
                            type: 'html',
                            message: `<div class="stopped-request-message"><b>${t('message.stopped_by_user')}</b></div>`,
                            source: 'frontend',
                        });

                        const requestIndex = index - 1;
                        if (requestIndex >= 0 && newState[requestIndex].type === 'request') {
                            if (!newState[requestIndex].parameters) {
                                newState[requestIndex].parameters = {};
                            }
                            newState[requestIndex].parameters.cancelled = 'Y';
                        }
                    } else {
                        newState.push({
                            uid: uid(),
                            type: 'response',
                            parts: [
                                {
                                    type: 'html',
                                    message: `<div class="stopped-request-message"><b>${t('message.stopped_by_user')}</b></div>`,
                                    source: 'frontend',
                                },
                            ],
                            parameters: {
                                cancelled: 'Y',
                            },
                        });
                    }

                    return newState;
                });
            };

            conciergeAPI
                .getAnswer(
                    backendMessage,
                    activeChat,
                    useStream,
                    onlySearch,
                    currentController,
                    currentSelectedTools,
                    {
                        ...extraUrlParams,
                        ...effectiveParams,
                    },
                    customInstructions,
                )
                .then(async (response) => {
                    const stream: ReadableStream = response.data;
                    const reader = stream.getReader();
                    const decoder = new TextDecoder();

                    let aborted = false;

                    const abortHandler = () => {
                        aborted = true;
                        reader.cancel();
                        responseCancelledByUser();
                    };
                    signal.addEventListener('abort', abortHandler);

                    try {
                        while (!aborted) {
                            const { done, value } = await reader.read();

                            if (value) {
                                const textChunk = decoder.decode(value, { stream: true });
                                if (textChunk) {
                                    responseToContent(textChunk);
                                }
                            }

                            if (done) {
                                const finalText = decoder.decode();
                                if (finalText) {
                                    responseToContent(finalText);
                                }
                                break;
                            }
                        }
                    } finally {
                        signal.removeEventListener('abort', abortHandler);
                        reader.releaseLock();
                        setLoading(false);
                    }
                })
                .catch(async (error) => {
                    console.error('Unexpected error during request:', error);
                    if (error.name === 'CanceledError') {
                        responseCancelledByUser();
                    } else {
                        let errorMessage = error.message || 'Unknown error';
                        const responseData = error.response?.data;

                        if (responseData) {
                            if (typeof responseData === 'string') {
                                errorMessage = responseData;
                            } else if (responseData instanceof ReadableStream) {
                                try {
                                    const reader = responseData.getReader();
                                    const decoder = new TextDecoder();
                                    let streamContent = '';
                                    while (true) {
                                        const { done, value } = await reader.read();
                                        if (done) break;
                                        streamContent += decoder.decode(value, { stream: true });
                                    }
                                    errorMessage =
                                        streamContent ||
                                        error.response?.statusText ||
                                        `Request failed with status ${error.response?.status}`;
                                } catch {
                                    errorMessage =
                                        error.response?.statusText ||
                                        `Request failed with status ${error.response?.status}`;
                                }
                            } else if (typeof responseData === 'object' && responseData.error) {
                                errorMessage = responseData.error;
                            } else if (typeof responseData === 'object' && responseData.message) {
                                errorMessage = responseData.message;
                            }
                        }

                        setDialog((prevState) => {
                            const newState = [...prevState];

                            const index = newState.findIndex((item) => item.uid == messageUID);
                            if (index > -1) {
                                const jsonPart: ErrorBlock = {
                                    error: errorMessage,
                                };

                                newState[index].parts.push({
                                    type: 'json',
                                    message: JSON.stringify(jsonPart, null, 2),
                                    jsonBlocks: [jsonPart],
                                    source: 'backend',
                                });
                            }

                            return newState;
                        });
                    }
                })
                .finally(() => {
                    if (onlySearch) {
                        dispatch(setOnlySearch(false));
                    }
                    setLastInteraction(Date.now());
                    setLoading(false);
                });
        },
        [
            activeChatRequest,
            activeChat,
            useStream,
            onlySearch,
            extraUrlParams,
            proceedMode,
            excludedTools,
            removeProceedButtons,
            responseToContent,
            dispatch,
            store,
            t,
            getContext,
            customInstructions,
        ],
    );

    const onNewRequest = useCallback(() => {
        const request = activeChatRequestRef.current;
        if (request > '') {
            const requestUid = uid();
            const responseUid = uid();

            requestUIDRef.current = requestUid;
            responseUIDRef.current = responseUid;

            setDialog((prevState) => {
                const newState = [...prevState];

                newState.push({
                    uid: requestUid,
                    type: 'request',
                    parts: [
                        {
                            type: 'html',
                            message: request,
                            source: 'frontend',
                        },
                    ],
                });

                newState.push({
                    uid: responseUid,
                    type: 'response',
                    parts: [],
                    parameters: {
                        requestPath: currentPath,
                        selectedDomains: selectedConciergeDomains,
                    },
                });

                return newState;
            });

            processRequest();
        }
    }, [currentPath, selectedConciergeDomains, processRequest]);

    useEffect(() => {
        onNewRequest();
    }, [activeChatRequest, activeChat]);

    const onRequestSubmit = useCallback(
        (request: string) => {
            if (request == '') return;

            if (activeChatRequestRef.current == request) {
                onNewRequest();
            }

            activeChatRequestRef.current = request;
            setActiveChatRequest(request);
        },
        [onNewRequest],
    );

    const clearActiveChatRequest = useCallback(() => {
        activeChatRequestRef.current = '';
        setActiveChatRequest('');
    }, []);

    const startNewChat = useCallback(() => {
        setActiveChat(0);
        clearActiveChatRequest();
        setDialog([]);
        setExcludedTools([]);
        setLastInteraction(Date.now());
        setProceedMode(false);
        if (isElementViewer) {
            const elementId = matches[matches.length - 1].params?.elementId as string;

            if (mappedDomains[elementId] && mappedDomains[elementId].length > 0) {
                dispatch(setSelectedDomains(mappedDomains[elementId]));
                dispatch(setDefaultSelectedDomains(mappedDomains[elementId]));
            } else {
                dispatch(setDefaultSelectedDomains(undefined));
            }
        } else {
            if (isAppPanel && selectedDomains !== undefined) {
                dispatch(setSelectedDomains(selectedDomains));
            } else {
                dispatch(setSelectedDomains([]));
            }
            dispatch(setDefaultSelectedDomains(undefined));
        }
    }, [isElementViewer, isAppPanel, selectedDomains, mappedDomains, matches, dispatch]);

    const onAbort = useCallback(() => {
        setLoading(false);
        currentController?.abort();
    }, []);

    const silentAbort = useCallback(() => {
        setLoading(false);
    }, []);

    const onResendRequest = useCallback(
        (excludedToolsFromAuth: string[]) => {
            setProceedMode(true);
            setExcludedTools(excludedToolsFromAuth);
            const newResponseUid = uid();
            responseUIDRef.current = newResponseUid;

            setDialog((prevState) => {
                const newState = [...prevState];

                newState.push({
                    uid: newResponseUid,
                    type: 'response',
                    parts: [],
                    parameters: {
                        requestPath: currentPath,
                        selectedDomains: selectedConciergeDomains,
                    },
                });

                return newState;
            });

            setTimeout(() => {
                processRequest({ proceed: '1' }, excludedToolsFromAuth);
            }, 50);
        },
        [currentPath, selectedConciergeDomains, processRequest],
    );

    // Handle visibility and auto-start new chat
    useEffect(() => {
        if (visible) {
            const numberOfMinutesBeforeStartNewChat = startNewChatAfter
                ? startNewChatAfter
                : conciergeSettings.number_of_minutes_before_start_new_chat;

            if (numberOfMinutesBeforeStartNewChat > 0) {
                const beforeStart = numberOfMinutesBeforeStartNewChat * 60 * 1000;
                const now = Date.now();
                const expiredOn = lastInteraction + beforeStart;

                if (now > expiredOn) {
                    startNewChat();
                }
            }
            let isSetDefaultSelectedDomains = false;
            if (isElementViewer && dialog.length === 0) {
                const elementId = matches[matches.length - 1].params?.elementId as string;

                if (mappedDomains[elementId] && mappedDomains[elementId].length > 0) {
                    dispatch(setSelectedDomains(mappedDomains[elementId]));
                    dispatch(setDefaultSelectedDomains(mappedDomains[elementId]));
                } else {
                    dispatch(setDefaultSelectedDomains(undefined));
                }
                isSetDefaultSelectedDomains = true;
            }
            if (
                isAppPanel &&
                selectedDomains !== undefined &&
                dialog.length === 0 &&
                selectedConciergeDomains.length === 0
            ) {
                dispatch(setSelectedDomains(selectedDomains));
                dispatch(setDefaultSelectedDomains(undefined));
                isSetDefaultSelectedDomains = true;
            }

            if (!isSetDefaultSelectedDomains) {
                dispatch(setDefaultSelectedDomains(undefined));
            }
        }
    }, [visible, isElementViewer, isAppPanel]);

    // Handle domains data
    useEffect(() => {
        if (!domainsData || !Array.isArray(domainsData)) {
            return;
        }

        const resultAvailableDomains = domainsData.filter((domain) => {
            if (domain.name === 'search' && includeContentSearch !== undefined) {
                return includeContentSearch;
            }

            if (domain.name === 'glossary' && includeGlossaryTerms !== undefined) {
                return includeGlossaryTerms;
            }

            if (domain.name === 'faq' && includeCustomFaq !== undefined) {
                return includeCustomFaq;
            }

            if (domain.name === 'docs' && includeHelpDocs !== undefined) {
                return includeHelpDocs;
            }

            if (availableDomains) {
                return availableDomains.includes(domain.name);
            }

            return true;
        });

        dispatch(setAvailableDomains(resultAvailableDomains));
    }, [domainsData, includeContentSearch, includeGlossaryTerms, includeCustomFaq, includeHelpDocs, availableDomains]);

    // Handle tools data
    useEffect(() => {
        if (toolsData && Array.isArray(toolsData)) {
            dispatch(setAvailableTools(toolsData));
        }
    }, [toolsData]);

    // Clean up only-status messages
    useEffect(() => {
        if (!loading && dialog.length > 0) {
            const lastMessage = dialog[dialog.length - 1];

            if (lastMessage.parts.length === 0) {
                return;
            }

            let onlyStatuses = true;

            lastMessage.parts.forEach((part) => {
                if (part.type === 'markdown' || part.type === 'html' || part.type === 'invalid-request') {
                    onlyStatuses = false;
                } else if (part.type === 'json') {
                    part.jsonBlocks?.forEach((block) => {
                        if (block.data_type !== 'status-badge' && block.data_type !== 'next-block') {
                            onlyStatuses = false;
                        }
                    });
                }
            });

            if (onlyStatuses) {
                const newDialog = dialog.slice(0, -1);
                setDialog(newDialog);
            }
        }
    }, [dialog, loading]);

    // Handle pending new chat
    useEffect(() => {
        if (pendingNewChat && domainsData && toolsData) {
            setActiveChatRequest(pendingNewChat);
            setPendingNewChat(null);
        }
    }, [pendingNewChat, domainsData, toolsData]);

    return {
        dialog,
        setDialog,
        loading,
        activeChat,
        setActiveChat,
        activeChatRequest,
        onRequestSubmit,
        startNewChat,
        onAbort,
        silentAbort,
        isElementViewer,
        mappedDomains,
        domainsData,
        toolsData,
        onResendRequest,
        setLastInteraction,
        clearActiveChatRequest,
    };
}

export interface NewChatHandler {
    handleNewChat: (newChat: string) => void;
    clearPendingNewChat: () => void;
}

export function useNewChatHandler(
    domainsData: IDomain[] | undefined,
    toolsData: ITool[] | undefined,
    setDialog: React.Dispatch<React.SetStateAction<IDialogItem[]>>,
    setActiveChat: React.Dispatch<React.SetStateAction<number>>,
    onRequestSubmit: (request: string) => void,
    dispatch: any,
): NewChatHandler {
    const [pendingNewChat, setPendingNewChat] = useState<string | null>(null);

    useEffect(() => {
        if (pendingNewChat && domainsData && toolsData) {
            onRequestSubmit(pendingNewChat);
            setPendingNewChat(null);
        }
    }, [pendingNewChat, domainsData, toolsData, onRequestSubmit]);

    const handleNewChat = useCallback(
        (newChat: string) => {
            setDialog([]);
            setActiveChat(Date.now());
            dispatch(setSelectedDomains([]));

            if (domainsData && toolsData) {
                onRequestSubmit(newChat);
            } else {
                setPendingNewChat(newChat);
            }
        },
        [domainsData, toolsData, setDialog, setActiveChat, onRequestSubmit, dispatch],
    );

    const clearPendingNewChat = useCallback(() => {
        setPendingNewChat(null);
    }, []);

    return {
        handleNewChat,
        clearPendingNewChat,
    };
}
